import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Universeel status-bolletje van de dubbele status-grendel.
 * GROEN = beschikbaar, BLAUW = 100% online (gereserveerd voor later),
 * ORANJE = verlof/beschikbaar vanaf datum, ROOD = volgeboekt/inactief.
 * Palet gelijk aan EmployerMapSearchPage.getAvailabilityDot / ReisradiusMap.
 */
export type PoolStatus = 'GROEN' | 'BLAUW' | 'ORANJE' | 'ROOD';

const DOT_CLASSES: Record<PoolStatus, string> = {
  GROEN: 'bg-green-500',
  BLAUW: 'bg-blue-500',
  ORANJE: 'bg-orange-400',
  ROOD: 'bg-red-500'
};

interface StatusDotProps {
  status: PoolStatus;
  /** ISO-datum (YYYY-MM-DD) — getoond bij ORANJE als "beschikbaar vanaf" */
  beschikbaarVanaf?: string | null;
  /** Toon het label naast het bolletje */
  withLabel?: boolean;
  className?: string;
}

const StatusDot: React.FC<StatusDotProps> = ({ status, beschikbaarVanaf, withLabel = false, className = '' }) => {
  const { t } = useTranslation('common');

  const labels: Record<PoolStatus, string> = {
    GROEN: t('poolStatus.groen', { defaultValue: 'Beschikbaar' }),
    BLAUW: t('poolStatus.blauw', { defaultValue: '100% online beschikbaar' }),
    ORANJE:
      status === 'ORANJE' && beschikbaarVanaf
        ? t('poolStatus.oranjeVanaf', {
            defaultValue: 'Beschikbaar vanaf {{datum}}',
            datum: new Date(beschikbaarVanaf).toLocaleDateString('nl-NL')
          })
        : t('poolStatus.oranje', { defaultValue: 'Tijdelijk met verlof' }),
    ROOD: t('poolStatus.rood', { defaultValue: 'Volgeboekt' })
  };

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={labels[status]}>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT_CLASSES[status]}`} aria-hidden="true" />
      {withLabel && <span className="text-sm text-gray-700">{labels[status]}</span>}
    </span>
  );
};

export default StatusDot;
